import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useAdmin } from '@/hooks/useAdmin';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import { BrandLogo } from '@/components/ui/brand-logo';
import { TierBadge } from '@/components/subscription/TierBadge';
import { FoundingMemberBadge } from '@/components/founding-member/FoundingMemberBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LogOut, Settings, MessageSquarePlus, Shield, Map, Building2, Users, Newspaper, MoreHorizontal } from 'lucide-react';
import { BETA_CONFIG } from '@/config/beta';
import { useTeamSubscription } from '@/hooks/useTeamSubscription';
import { ValuesBadge } from '@/components/values/ValuesBadge';
import { useUnreadChangelog } from '@/hooks/useUnreadChangelog';

interface DashboardHeaderProps {
  onOpenWizard?: () => void;
  valuesComplete?: boolean;
}

type Tier = "free" | "pro" | "beta_pro";

export const DashboardHeader = ({ onOpenWizard, valuesComplete = false }: DashboardHeaderProps) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { isAdmin } = useAdmin();
  const { toast } = useToast();
  const { isPro } = useSubscription();
  const { isTeamAdmin, teamSubscription } = useTeamSubscription();
  const { hasUnread } = useUnreadChangelog();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Derive tier and expiration from cached profile
  const { tier, tierExpiresAt } = useMemo(() => {
    if (!profile) return { tier: 'free' as Tier, tierExpiresAt: null as Date | null };

    if (isPro) {
      return {
        tier: 'pro' as Tier,
        tierExpiresAt: profile.premium_until ? new Date(profile.premium_until) : null,
      };
    }

    if (profile.premium_until) {
      const premiumUntil = new Date(profile.premium_until);
      if (premiumUntil > new Date()) {
        return { tier: 'pro' as Tier, tierExpiresAt: premiumUntil };
      }
    }

    if (profile.beta_pro_expires_at) {
      const expiresAt = new Date(profile.beta_pro_expires_at);
      if (expiresAt > new Date()) {
        return { tier: 'beta_pro' as Tier, tierExpiresAt: expiresAt };
      }
    }

    return { tier: (profile.tier as Tier) || 'free', tierExpiresAt: null as Date | null };
  }, [profile, isPro]);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast({
        title: 'Error signing out',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleLogoClick = () => {
    if (valuesComplete) {
      navigate('/home');
    } else {
      navigate('/dashboard');
    }
  };

  const handleTierBadgeClick = () => {
    if (!BETA_CONFIG.isBetaMode && tier === 'free') {
      navigate('/pricing');
    } else {
      navigate('/settings');
    }
  };

  return (
    <>
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm focus:font-medium"
    >
      Skip to content
    </a>
    <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="container py-4 flex items-center justify-between overflow-hidden">
        <button 
          onClick={handleLogoClick}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
        >
          <BrandLogo size="md" />
        </button>
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Tier Badge */}
          <div className="hidden sm:block">
            <TierBadge
              tier={tier}
              size="sm"
              expiresAt={tierExpiresAt}
              onClick={handleTierBadgeClick}
            />
          </div>
          <div className="hidden sm:block">
            <FoundingMemberBadge size="sm" variant="pill" />
          </div>
          {valuesComplete && (
            <div className="hidden sm:block">
              <ValuesBadge />
            </div>
          )}

          {/* User name - hidden on mobile */}
          <span className="text-sm text-muted-foreground hidden lg:block max-w-[150px] truncate">
            {profile?.first_name || (profile?.display_name && !profile.display_name.includes('@') ? profile.display_name.split(' ')[0] : null) || user?.email?.split('@')[0]}
          </span>
          
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              title="Admin Dashboard"
              aria-label="Admin Dashboard"
              className="min-h-[44px] min-w-[44px]"
            >
              <Shield className="w-4 h-4" />
            </Button>
          )}
          {isTeamAdmin && teamSubscription && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/team/${teamSubscription.team_slug}`)}
              title="Team Dashboard"
              aria-label="Team Dashboard"
              className="min-h-[44px] min-w-[44px]"
            >
              <Building2 className="w-4 h-4" />
            </Button>
          )}
          {/* More dropdown — groups secondary actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" title="More" aria-label="More actions" className="relative hidden sm:inline-flex min-h-[44px] min-w-[44px]">
                <MoreHorizontal className="w-4 h-4" />
                {hasUnread && <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate('/network')}>
                <Users className="w-4 h-4 mr-2" /> Network
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setFeedbackOpen(true)}>
                <MessageSquarePlus className="w-4 h-4 mr-2" /> Submit Feedback
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/changelog')}>
                <Newspaper className="w-4 h-4 mr-2" /> What's New
                {hasUnread && <span className="ml-auto w-2 h-2 bg-primary rounded-full" />}
              </DropdownMenuItem>
              {onOpenWizard && (
                <DropdownMenuItem onClick={onOpenWizard}>
                  <Map className="w-4 h-4 mr-2" /> Journey Guide
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
            title="Settings"
            aria-label="Settings"
            data-tour="settings-link"
            className="min-h-[44px] min-w-[44px]"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSignOut}
            title="Sign out"
            aria-label="Sign out"
            className="min-h-[44px] min-w-[44px]"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </header>
    </>
  );
};
